/**
 * Retry com backoff exponencial para requisições de autenticação
 */

type RetryConfig = {
  maxAttempts: number;
  initialDelayMs: number;
  maxDelayMs: number;
  backoffMultiplier: number;
};

const DEFAULT_CONFIG: RetryConfig = {
  maxAttempts: 3,
  initialDelayMs: 500,
  maxDelayMs: 3000,
  backoffMultiplier: 1.5,
};

export async function withRetry<T>(
  fn: () => Promise<T>,
  config: Partial<RetryConfig> = {}
): Promise<T> {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  let lastError: any;
  let delay = cfg.initialDelayMs;

  for (let attempt = 1; attempt <= cfg.maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err: any) {
      lastError = err;
      
      // Não faz retry em erros que não devem ser retentados
      const status = err?.status || 0;
      const isRetryable = (
        status === 0 || // Network error
        status === 408 || // Request Timeout
        status >= 500 // Server errors
      );
      
      if (!isRetryable || attempt === cfg.maxAttempts) {
        throw err;
      }
      
      // Aguarda antes de tentar novamente
      await new Promise(resolve => setTimeout(resolve, delay));
      delay = Math.min(delay * cfg.backoffMultiplier, cfg.maxDelayMs);
    }
  }

  throw lastError;
}

/**
 * Melhora e formata mensagens de erro de autenticação
 */
export function formatAuthErrorMessage(err: any): {
  title: string;
  description: string;
  retryable: boolean;
  retrySeconds?: number;
} {
  const message = err?.message || '';
  const code = err?.code || '';
  const status = err?.status || 0;
  
  // Rate limit
  if (status === 429 || code.includes('rate_limit')) {
    const retrySeconds = Number(err?.retryAfter) || 60;
    return {
      title: '⏱️ Muitas tentativas',
      description: `Aguarde ${retrySeconds}s antes de tentar novamente.`,
      retryable: false,
      retrySeconds,
    };
  }
  
  // Network error
  if (status === 0 || message.toLowerCase().includes('network')) {
    return {
      title: '🌐 Problema de conexão',
      description: 'Verifique sua internet e tente novamente.',
      retryable: true,
    };
  }
  
  // PKCE/OAuth error
  if (message.includes('code challenge') || message.includes('code verifier')) {
    return {
      title: '🔐 Erro de autenticação',
      description: 'Tente fazer login novamente. Limparemos a sessão anterior.',
      retryable: true,
    };
  }
  
  // Invalid credentials
  if (status === 401 || code === 'invalid_grant' || message.includes('credentials')) {
    return {
      title: '❌ Credenciais inválidas',
      description: 'Verifique seu email e senha.',
      retryable: false,
    };
  }
  
  // User not found
  if (message.toLowerCase().includes('user not found')) {
    return {
      title: '👤 Usuário não encontrado',
      description: 'Verifique seu email ou crie uma nova conta.',
      retryable: false,
    };
  }
  
  // Email not confirmed
  if (message.includes('email not confirmed') || message.includes('not confirmed')) {
    return {
      title: '📧 Email não confirmado',
      description: 'Verifique seu email para o link de confirmação.',
      retryable: false,
    };
  }
  
  // Generic server error
  if (status >= 500) {
    return {
      title: '⚠️ Erro no servidor',
      description: 'Tente novamente em alguns momentos.',
      retryable: true,
    };
  }
  
  // Default
  return {
    title: '❌ Erro ao fazer login',
    description: message || 'Tente novamente.',
    retryable: true,
  };
}
